import { HomeSalesSummary } from './HomeSalesSummary';
/**
 * DashboardHome — the shared Home surface: Coming up leads, with Pipeline,
 * Profit & Loss and Outstanding stepped back in a quieter rail beside it.
 * Rendered by QSDashboard (workspace-wide CG ops view) and by the workspace
 * member portal (features/client ClientOverview), the latter scoped to a single
 * member's own projects. Keeping the layout here is what stops the two Home
 * surfaces drifting apart — the only difference between them is the data passed in.
 *
 * Uses a fixed layout across users, without personal ordering or visibility.
 */

import { CSSProperties, useMemo } from 'react';
import { useNativeBridge } from '../../hooks/useNativeBridge';
import { useAuth } from '../../platform/auth/AuthContext';
import { useFieldOnlyMember } from '../../hooks/useFieldOnlyMember';
import { cn } from '../../shared/ui/utils';
import {
  CollectionTemplate,
  TemplateSection,
  useAppShellFrame,
  useTabletSidebarLayout,
} from '../../layout-system';
import type { Project, ContractItem, InterimApplication } from '../../types/database';
import type { Task } from '../../types/tasks';

import { DashboardCard } from './DashboardCard';
import { PipelineBars } from './PipelineBars';
import { ProfitLossCard } from './ProfitLossCard';
import { ActionQueue } from './ActionQueue';
import { useExpiringSiteCards } from './useExpiringSiteCards';
import { useExpiringSubcontractorCover } from './useExpiringSubcontractorCover';
import { useLolerPlantAssets } from './useLolerPlantAssets';
import { useDraftCisReturns } from './useDraftCisReturns';
import { useSubmittedExpenseClaims } from './useSubmittedExpenseClaims';
import { useUnpaidSalesInvoices } from './useUnpaidSalesInvoices';
import { moreReadWorkspaces } from '../../hooks/moreScreenWarm';
import { useIssuedRfis } from './useIssuedRfis';
import { useEnquiryDeals } from './useEnquiryDeals';
import { useWorkspaceNavigation } from '../../hooks/useWorkspaceNavigation';
import { useUnsignedDayworkTallies } from './useUnsignedDayworkTallies';
import { enquiriesBoardGranted, homeReadWorkspaces } from './homeComingUpReads';
import { useOutstandingSnags } from '../snagging/useOutstandingSnags';
import { OutstandingBars } from './OutstandingBars';
import { openProjectValuations } from './openProjectValuations';
import { DashboardListSkeleton } from './DashboardLoading';

interface DashboardHomeProps {
  /** Raw project/task lists — archived projects and done/cancelled tasks are
   *  filtered out here so callers pass their scoped data unfiltered. */
  projects: Project[];
  tasks: Task[];
  contractItems: ContractItem[];
  interimApplications: InterimApplication[];
  salesWorkspaceId?: string;
  /** Lists lapsing site cards in "Coming up". Only the ops dashboard passes it. */
  siteCardsWorkspaceId?: string;
  loading?: boolean;
}

export function DashboardHome({
  projects,
  tasks,
  contractItems,
  interimApplications,
  salesWorkspaceId,
  siteCardsWorkspaceId,
  loading = false,
}: DashboardHomeProps) {
  const { isShellLocked } = useAppShellFrame({
    currentView: 'dashboard',
    scrollMode: 'platform',
    noPadding: true,
  });
  const auth = useAuth();
  const usesTabletSidebarLayout = useTabletSidebarLayout();
  // Field members only get the site list; the commercial cards are dropped.
  const fieldOnly = useFieldOnlyMember();

  // iPhone large-title pattern: the native bar's inline title is blank at rest
  // on Home (navigation.ts sends largeTitle: true), so the web renders the big
  // left-aligned title here at the top of the scroll content. It scrolls away as
  // the native centre title cross-fades in.
  //
  // iPHONE ONLY, via hasNativeTabBar (true only there; iPad reports
  // hasNativeSidebar instead). iPad has a real UIKit navigation bar of its own,
  // so drawing this as well stacked a second title BELOW the bar and its
  // hairline — the "why is the header so low, it's like a line break" report.
  // There the title belongs in the bar, and navigation.ts stops blanking it.
  const nativeBridge = useNativeBridge();
  const showLargeTitle = nativeBridge.platform === 'ios' && nativeBridge.hasNativeTabBar;

  const siteCards = useExpiringSiteCards(siteCardsWorkspaceId);
  const plantAssets = useLolerPlantAssets(siteCardsWorkspaceId);
  // Same roles and workspace as site cards: both are workspace-admin reads the server refuses to anyone else.
  const subcontractorCover = useExpiringSubcontractorCover(siteCardsWorkspaceId);
  // Gated in one place, so the background warm reads exactly what Home draws.
  const readWorkspaces = homeReadWorkspaces(auth);
  const cisReturns = useDraftCisReturns(readWorkspaces.cis);
  const expenseClaims = useSubmittedExpenseClaims(readWorkspaces.expenses);
  const rfis = useIssuedRfis(readWorkspaces.rfis);
  const unsignedDayworks = useUnsignedDayworkTallies(readWorkspaces.dayworks);
  // The Enquiries board is a grant, not a role: read its deals only when the rail would show it, as the board does.
  const navigation = useWorkspaceNavigation();
  // The Sales register's own gate, so Home never reads invoices for someone who could not open Sales.
  const salesInvoices = useUnpaidSalesInvoices(moreReadWorkspaces(auth, navigation).salesInvoices);
  const enquiriesGranted = enquiriesBoardGranted(navigation, auth.isClient());
  const enquiryDeals = useEnquiryDeals(
    enquiriesGranted && !fieldOnly ? auth.activeWorkspaceId || auth.user?.workspaceId || auth.user?.workspace_id || undefined : undefined,
  );

  const activeProjects = useMemo(
    () => projects.filter((p) => p.archiveStatus !== 'archived'),
    [projects],
  );

  // A Field member's list is their own work: other people's tasks on the
  // same site (commercial chases, tenders) are not theirs to see.
  const currentUserId = auth.user?.id;
  const activeTasks = useMemo(
    () => tasks.filter((t) => t.status !== 'cancelled' && t.status !== 'completed'
      && (!fieldOnly || (t.assigned_to ?? (t as { assignedTo?: string }).assignedTo) === currentUserId)),
    [tasks, fieldOnly, currentUserId],
  );

  // Only projects in their defects period can show a retention release row. The route answers with the projects this
  // user may read, so a project outside that set simply has no tally and no snag note.
  const dlpWorkspaceIds = useMemo(
    () => (fieldOnly ? [] : activeProjects
      .filter((p) => p.stage === 'defects_liability')
      .map((p) => (p as { workspaceId?: string }).workspaceId)
      .filter((id): id is string => !!id)),
    [activeProjects, fieldOnly],
  );
  const dlpSnags = useOutstandingSnags(dlpWorkspaceIds, dlpWorkspaceIds.length > 0);
  const outstandingSnags = useMemo(() => Object.fromEntries(dlpSnags.tallies), [dlpSnags]);

  const comingUpRows = usesTabletSidebarLayout ? 5 : 10;
  const comingUp = (
    <DashboardCard title="Coming up" bare lead className="[&>div:first-child]:px-3 @[700px]/dash:[&>div:first-child]:px-4" bodyClassName="[&>div]:px-0">
      {loading ? (
        <DashboardListSkeleton rows={comingUpRows} />
      ) : (
        <ActionQueue
          tasks={activeTasks}
          contractItems={contractItems}
          applications={interimApplications}
          projects={activeProjects}
          siteCards={siteCards}
          subcontractorCover={subcontractorCover}
          plantAssets={plantAssets}
          cisReturns={cisReturns}
          expenseClaims={expenseClaims}
          salesInvoices={salesInvoices.invoices}
          invoiceChases={salesInvoices.chases}
          unsignedDayworks={unsignedDayworks}
          outstandingSnags={outstandingSnags}
          rfis={rfis}
          enquiryDeals={enquiryDeals}
          userId={auth.user?.id ?? ''}
          maxRows={comingUpRows}
          siteOnly={fieldOnly}
        />
      )}
    </DashboardCard>
  );

  // The figures behind the work: glanced at, not acted on, so they keep the
  // small muted label and shorter charts.
  const glance = (
    <>
      <DashboardCard title="Pipeline" hint="by stage" bare bodyClassName="[&>div]:px-0">
        <PipelineBars projects={activeProjects} list loading={loading} />
      </DashboardCard>
      <DashboardCard title="Profit & Loss" hint="last 6 months" bare bodyClassName="h-[160px] pt-3 [&>div]:px-0">
        <ProfitLossCard />
      </DashboardCard>
      <DashboardCard title="Outstanding" hint="certified vs paid" bare bodyClassName="min-h-[140px] [&>div]:px-0 [&_p.bg-card]:bg-transparent">
        {loading ? (
          <DashboardListSkeleton rows={4} bars />
        ) : (
          <OutstandingBars projects={activeProjects} applications={interimApplications} onOpen={(projectId) => openProjectValuations(projectId, activeProjects)} />
        )}
      </DashboardCard>
      {salesWorkspaceId && <HomeSalesSummary key={`${auth.user?.id}:${salesWorkspaceId}`} workspaceId={salesWorkspaceId} bare embedded />}
    </>
  );

  return (
    <CollectionTemplate fillHeight inset="flush" contentClassName="h-full min-h-0">
      <TemplateSection
        surface="plain"
        className="flex-1 min-h-0"
        contentClassName={cn(
          'flex min-h-0 flex-col',
          isShellLocked ? 'h-full overflow-hidden' : 'h-auto',
        )}
      >
        {loading && <span role="status" className="sr-only">Loading dashboard</span>}
        <div
          aria-busy={loading}
          className={cn(
            '@container/dash relative flex min-h-0 flex-col w-full mx-auto gap-3 @[700px]/dash:gap-4',
            // Locked workspaces scroll this content surface. On mobile the
            // shell outlet is already the scroll owner, so a nested scroller
            // would trap touch gestures.
            isShellLocked
              ? 'h-full flex-1 overflow-y-auto overscroll-contain'
              : 'h-auto overflow-visible',
          )}
          style={
            {
              overflowX: 'clip',
              touchAction: 'pan-y',
              // Longhand on every side — mixing the `padding` shorthand with
              // paddingTop/paddingBottom warns on rerender (shorthand vs
              // non-shorthand conflict) because the bottom clearance is dynamic.
              paddingLeft: '16px',
              paddingRight: '16px',
              // The shell reserves the WEB floating-header clearance on
              // .qs-shell-content-inner, so on web only the breathing gap is
              // needed here (adding the web clearance again double-counts it);
              // the gap matches the sides, since 4px put the first card hard
              // against the header band. But on the native iOS shell the shell
              // reserves content-top-offset (0px) instead of the native nav-bar
              // height, so the first card row underlapped the bar.
              // --qs-native-header-clearance is the native bar height on the
              // iOS shell and 0px everywhere else, so it clears the bar
              // natively without double-counting on web/desktop.
              paddingTop: 'calc(var(--qs-native-header-clearance, 0px) + 16px)',
              paddingBottom: isShellLocked
                ? usesTabletSidebarLayout ? '16px' : '8px'
                : 'calc(var(--qs-bottom-clearance-compact, var(--qs-mobile-bottom-nav-height, calc(var(--qs-mobile-bottom-nav-min-height, 55px) + var(--qs-mobile-bottom-nav-safe-floor, 34px)))) + 8px)',
            } as CSSProperties
          }
        >
          {/* iOS large title: big left-aligned, scrolls away as the native inline
              title cross-fades in (see navigation.ts largeTitle + the iOS shell). */}
          {showLargeTitle && (
            <h1 className="text-[34px] font-bold leading-tight tracking-[-0.022em] text-foreground font-apple">
              Home
            </h1>
          )}

          {fieldOnly ? (
            <section className="min-w-0">{comingUp}</section>
          ) : (
            // Coming up is the focal point: the one list that asks for action.
            // Each column is its own dash container so the rail's charts size
            // to the rail, not to the whole page.
            <div className="grid min-w-0 shrink-0 grid-cols-1 gap-x-12 gap-y-10 @[880px]/dash:grid-cols-[minmax(0,1.6fr)_minmax(300px,1fr)]">
              <section aria-label="Coming up" className="@container/dash min-w-0">{comingUp}</section>
              <aside aria-label="At a glance" className="@container/dash flex min-w-0 flex-col gap-7">{glance}</aside>
            </div>
          )}
        </div>
      </TemplateSection>

    </CollectionTemplate>
  );
}
